import { SyntheticEvent, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import PrintIcon from '@mui/icons-material/Print'
import { skipToken } from '@reduxjs/toolkit/query'
import { useNavigate, Outlet } from 'react-router-dom'

import BasicTabs from 'components/tabs/basic-tabs/basic-tabs.component'
import TabPanel from 'components/tabs/tab-panel/tab-panel.component'
import TwoColumnLayout from 'layouts/two-column-layout/two-column-layout'
import {
  useCreateExpenseMutation,
  useDeleteExpenseMutation,
  useGetExpenseSummariesQuery,
  useUpdateExpenseStatusMutation,
} from 'store/expenses/expenses.api'
import { ExpenseSummary } from 'store/expenses/expenses.types'
import { getUserId } from 'utils/common'
import { exportVendorPayablesToCSV } from 'utils/csv-export'

import { payablesTabs } from './payables.constants'
import {
  fileNameFromS3Path,
  formatExpenseAmount,
  formatExpenseDate,
} from './payables.utils'
import PayableUploadDialog from './payable-upload-dialog'

const PayablesPage = () => {
  const navigate = useNavigate()
  const userId = getUserId()
  const [tabsValue, setTabsValue] = useState(0)
  const [selectedVendor, setSelectedVendor] = useState<string | null>(null)
  const [listSearch, setListSearch] = useState('')
  const [tableSearch, setTableSearch] = useState('')
  const [uploadOpen, setUploadOpen] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [markingPaidId, setMarkingPaidId] = useState<string | null>(null)
  const [errorOpen, setErrorOpen] = useState(false)
  const [errorMessage, setErrorMessage] = useState(
    'Please select at least one invoice to export.'
  )

  const isUnpaidTab = tabsValue === 0

  const {
    data: unpaidExpenses = [],
    isLoading: isLoadingUnpaid,
    refetch: refetchUnpaid,
  } = useGetExpenseSummariesQuery(userId && isUnpaidTab ? { userId } : skipToken)

  const hasProcessing = unpaidExpenses.some(
    (expense) => expense.status === 'PROCESSING'
  )

  // Shared cache key with the query above; enables polling only while extracting.
  useGetExpenseSummariesQuery(userId && isUnpaidTab ? { userId } : skipToken, {
    pollingInterval: hasProcessing ? 4000 : 0,
  })

  const {
    data: paidExpenses = [],
    isLoading: isLoadingPaid,
    refetch: refetchPaid,
  } = useGetExpenseSummariesQuery(
    userId && !isUnpaidTab ? { userId, status: 'PAID' } : skipToken
  )

  const [updateStatus] = useUpdateExpenseStatusMutation()
  const [createExpense, { isLoading: isUploading }] = useCreateExpenseMutation()
  const [deleteExpense, { isLoading: isDeleting }] = useDeleteExpenseMutation()
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const expenses = useMemo(() => {
    if (!isUnpaidTab) return paidExpenses

    const processing: ExpenseSummary[] = []
    const failed: ExpenseSummary[] = []
    const notPaid: ExpenseSummary[] = []
    for (const expense of unpaidExpenses) {
      if (expense.status === 'PROCESSING') processing.push(expense)
      else if (expense.status === 'FAILED') failed.push(expense)
      else if (expense.status === 'NOT_PAID') notPaid.push(expense)
    }
    // Processing first, then failed, then unpaid
    return [...processing, ...failed, ...notPaid]
  }, [isUnpaidTab, unpaidExpenses, paidExpenses])

  const isLoading = isUnpaidTab ? isLoadingUnpaid : isLoadingPaid
  const refetch = isUnpaidTab ? refetchUnpaid : refetchPaid

  const vendorNames = useMemo(() => {
    const names = Array.from(
      new Set(
        expenses
          .filter(
            (expense) =>
              expense.status !== 'PROCESSING' && expense.status !== 'FAILED'
          )
          .map((expense) => expense.name?.trim())
          .filter((name): name is string => Boolean(name))
      )
    ).sort((a, b) => a.localeCompare(b))
    return names
  }, [expenses])

  const filteredVendors = useMemo(() => {
    const query = listSearch.trim().toLowerCase()
    if (!query) return vendorNames
    return vendorNames.filter((name) => name.toLowerCase().includes(query))
  }, [listSearch, vendorNames])

  const filteredExpenses = useMemo(() => {
    let rows = expenses
    if (selectedVendor) {
      rows = rows.filter(
        (expense) =>
          expense.status === 'PROCESSING' ||
          expense.status === 'FAILED' ||
          expense.name === selectedVendor
      )
    }
    const query = tableSearch.trim().toLowerCase()
    if (!query) return rows
    return rows.filter((expense) => {
      const fileName = fileNameFromS3Path(expense.s3Path)
      const haystack = [
        expense.invoiceNumber,
        expense.name,
        fileName,
        expense.status === 'FAILED' ? 'failed' : '',
        formatExpenseDate(expense.invoiceDate),
        formatExpenseDate(expense.dueDate),
        formatExpenseAmount(expense.totalAmount, expense.currency),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return haystack.includes(query)
    })
  }, [expenses, selectedVendor, tableSearch])

  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    setTabsValue(newValue)
    setSelectedVendor(null)
    setSelectedIds(new Set())
    setTableSearch('')
  }

  const handleMarkAsPaid = async (expense: ExpenseSummary) => {
    setMarkingPaidId(expense.id)
    try {
      await updateStatus({ id: expense.id, status: 'PAID' }).unwrap()
      refetch()
    } finally {
      setMarkingPaidId(null)
    }
  }

  const handleDeleteFailed = async (expense: ExpenseSummary) => {
    setDeletingId(expense.id)
    try {
      await deleteExpense({ expenseId: expense.id }).unwrap()
      refetch()
    } finally {
      setDeletingId(null)
    }
  }

  const handleUpload = async (file: File) => {
    if (!userId) return
    await createExpense({
      userId,
      payableType: 'Vendor',
      file,
    }).unwrap()
    setUploadOpen(false)
    refetch()
  }

  const toggleSelected = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (checked) next.add(id)
      else next.delete(id)
      return next
    })
  }

  const filteredSelectable = useMemo(
    () =>
      filteredExpenses.filter(
        (expense) =>
          expense.status !== 'PROCESSING' && expense.status !== 'FAILED'
      ),
    [filteredExpenses]
  )

  const allSelected =
    filteredSelectable.length > 0 &&
    filteredSelectable.every((expense) => selectedIds.has(expense.id))

  const totalInvoiceAmount = useMemo(() => {
    const rowsToSum =
      selectedIds.size > 0
        ? filteredSelectable.filter((expense) => selectedIds.has(expense.id))
        : filteredSelectable
    return rowsToSum.reduce(
      (acc, expense) => acc + (expense.totalAmount ?? 0),
      0
    )
  }, [filteredSelectable, selectedIds])

  const currency =
    filteredSelectable[0]?.currency || expenses[0]?.currency || 'CAD'

  const handleExportCSV = () => {
    if (selectedIds.size === 0) {
      setErrorMessage('Please select at least one invoice to export.')
      setErrorOpen(true)
      return
    }
    exportVendorPayablesToCSV(expenses, selectedIds)
  }

  const selectionPanel = (
    <BasicTabs
      tabs={payablesTabs}
      value={tabsValue}
      handleChange={handleTabChange}
      scrollableContent
    >
      <TabPanel value={tabsValue} index={0}>
        <PayablesVendorList
          search={listSearch}
          onSearchChange={setListSearch}
          vendors={filteredVendors}
          selectedVendor={selectedVendor}
          onSelectVendor={setSelectedVendor}
          onUpload={() => setUploadOpen(true)}
        />
      </TabPanel>
      <TabPanel value={tabsValue} index={1}>
        <PayablesVendorList
          search={listSearch}
          onSearchChange={setListSearch}
          vendors={filteredVendors}
          selectedVendor={selectedVendor}
          onSelectVendor={setSelectedVendor}
          onUpload={() => setUploadOpen(true)}
        />
      </TabPanel>
    </BasicTabs>
  )

  const detailsPanel = (
    <Stack
      sx={{
        flex: 1,
        minHeight: 0,
        maxHeight: '100%',
        overflow: 'hidden',
        justifyContent: 'space-between',
      }}
    >
      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        sx={{ p: 2, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}
      >
        <TextField
          placeholder="Search"
          size="small"
          value={tableSearch}
          onChange={(e) => setTableSearch(e.target.value)}
          sx={{ minWidth: 220 }}
        />
      </Stack>

      <TableContainer sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allSelected}
                    indeterminate={selectedIds.size > 0 && !allSelected}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedIds(
                          new Set(filteredSelectable.map((expense) => expense.id))
                        )
                      } else {
                        setSelectedIds(new Set())
                      }
                    }}
                  />
                </TableCell>
                <TableCell>Invoice#</TableCell>
                <TableCell>Vendor</TableCell>
                <TableCell>Invoice Date</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Documents</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredExpenses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="text.secondary" sx={{ py: 4 }}>
                      No payables found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredExpenses.map((expense) => {
                  if (expense.status === 'PROCESSING') {
                    return (
                      <ProcessingExpenseRow key={expense.id} expense={expense} />
                    )
                  }
                  if (expense.status === 'FAILED') {
                    return (
                      <FailedExpenseRow
                        key={expense.id}
                        expense={expense}
                        deleting={deletingId === expense.id || isDeleting}
                        onDelete={() => handleDeleteFailed(expense)}
                      />
                    )
                  }
                  return (
                    <TableRow key={expense.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedIds.has(expense.id)}
                          onChange={(e) =>
                            toggleSelected(expense.id, e.target.checked)
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Typography
                          component="button"
                          onClick={() => navigate(`/vendor/${expense.id}`)}
                          sx={{
                            border: 0,
                            background: 'none',
                            color: 'primary.main',
                            cursor: 'pointer',
                            p: 0,
                            font: 'inherit',
                            textDecoration: 'underline',
                          }}
                        >
                          {expense.invoiceNumber || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>{expense.name || '—'}</TableCell>
                      <TableCell>
                        {formatExpenseDate(expense.invoiceDate)}
                      </TableCell>
                      <TableCell>
                        {formatExpenseDate(expense.dueDate)}
                      </TableCell>
                      <TableCell>
                        {formatExpenseAmount(
                          expense.totalAmount,
                          expense.currency
                        )}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => navigate(`/vendor/${expense.id}`)}
                          >
                            View
                          </Button>
                          {isUnpaidTab && (
                            <Button
                              size="small"
                              variant="contained"
                              disabled={markingPaidId === expense.id}
                              onClick={() => handleMarkAsPaid(expense)}
                            >
                              Mark as Paid
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          flexShrink: 0,
        }}
      >
        <Box />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            Total Invoice Amount: {currency} {totalInvoiceAmount.toFixed(2)}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handleExportCSV}
          >
            Export
          </Button>
        </Box>
      </Box>
    </Stack>
  )

  return (
    <>
      <TwoColumnLayout>
        {selectionPanel}
        {detailsPanel}
        {selectionPanel}
      </TwoColumnLayout>
      <PayableUploadDialog
        open={uploadOpen}
        loading={isUploading}
        onClose={() => setUploadOpen(false)}
        onUpload={handleUpload}
      />
      <Snackbar
        open={errorOpen}
        autoHideDuration={4000}
        onClose={() => setErrorOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setErrorOpen(false)}
          severity="error"
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
      <Outlet />
    </>
  )
}

type ProcessingExpenseRowProps = {
  expense: ExpenseSummary
}

const ProcessingExpenseRow = ({ expense }: ProcessingExpenseRowProps) => {
  const fileName = fileNameFromS3Path(expense.s3Path) || 'Uploading…'

  return (
    <>
      <TableRow
        sx={{
          bgcolor: 'action.hover',
          '& td': { borderBottom: 'none' },
        }}
      >
        <TableCell padding="checkbox" rowSpan={2} sx={{ verticalAlign: 'top' }}>
          <Checkbox disabled checked={false} />
        </TableCell>
        <TableCell sx={{ py: 1.5, pb: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {fileName}
          </Typography>
        </TableCell>
        <TableCell sx={{ py: 1.5, pb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Processing
          </Typography>
        </TableCell>
        <TableCell sx={{ py: 1.5, pb: 0.5 }} />
        <TableCell sx={{ py: 1.5, pb: 0.5 }} />
        <TableCell sx={{ py: 1.5, pb: 0.5 }} />
        <TableCell rowSpan={2} />
      </TableRow>
      <TableRow
        sx={{
          bgcolor: 'action.hover',
          '& td': { borderBottomColor: 'divider' },
        }}
      >
        <TableCell colSpan={5} sx={{ pt: 0, pb: 1.5 }}>
          <LinearProgress
            sx={{
              height: 6,
              borderRadius: 1,
              width: '100%',
            }}
          />
        </TableCell>
      </TableRow>
    </>
  )
}

type FailedExpenseRowProps = {
  expense: ExpenseSummary
  deleting: boolean
  onDelete: () => void
}

const FailedExpenseRow = ({
  expense,
  deleting,
  onDelete,
}: FailedExpenseRowProps) => {
  const fileName = fileNameFromS3Path(expense.s3Path) || '—'

  return (
    <TableRow hover>
      <TableCell padding="checkbox">
        <Checkbox disabled checked={false} />
      </TableCell>
      <TableCell>
        <Typography variant="body2">{fileName}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 600 }}>
          Failed
        </Typography>
      </TableCell>
      <TableCell>—</TableCell>
      <TableCell>—</TableCell>
      <TableCell>—</TableCell>
      <TableCell>
        <Button
          size="small"
          variant="contained"
          color="error"
          disabled={deleting}
          onClick={onDelete}
        >
          Delete
        </Button>
      </TableCell>
    </TableRow>
  )
}

type PayablesVendorListProps = {
  search: string
  onSearchChange: (value: string) => void
  vendors: string[]
  selectedVendor: string | null
  onSelectVendor: (vendor: string | null) => void
  onUpload: () => void
}

const PayablesVendorList = ({
  search,
  onSearchChange,
  vendors,
  selectedVendor,
  onSelectVendor,
  onUpload,
}: PayablesVendorListProps) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      minHeight: 0,
      p: 1.5,
      gap: 1.5,
    }}
  >
    <TextField
      placeholder="Search"
      size="small"
      fullWidth
      value={search}
      onChange={(e) => onSearchChange(e.target.value)}
    />
    <List sx={{ flex: 1, minHeight: 0, overflow: 'auto', py: 0 }}>
      <ListItemButton
        selected={selectedVendor === null}
        onClick={() => onSelectVendor(null)}
        sx={{ borderRadius: 1, mb: 0.5 }}
      >
        <ListItemText primary="All" />
      </ListItemButton>
      {vendors.map((vendor) => (
        <ListItemButton
          key={vendor}
          selected={selectedVendor === vendor}
          onClick={() => onSelectVendor(vendor)}
          sx={{ borderRadius: 1, mb: 0.5 }}
        >
          <ListItemText primary={vendor} />
        </ListItemButton>
      ))}
    </List>
    <Button variant="contained" fullWidth onClick={onUpload}>
      + Upload
    </Button>
  </Box>
)

export default PayablesPage
